"use client";

import Link from "next/link";
import { FormEvent, useEffect, useState } from "react";
import { BookOpen, Search } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import {
  ContentCategory,
  ExpertContent,
  searchContents,
} from "@/features/content/api";

const labels: Record<ContentCategory, string> = {
  HEALTH: "건강",
  VACCINATION: "예방접종",
  TRAINING: "훈련",
  NUTRITION: "영양",
  WELFARE: "동물복지",
};

export default function GuidesPage() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [items, setItems] = useState<ExpertContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  async function load(q = query, selected = category) {
    setLoading(true);
    setError("");
    try {
      setItems(await searchContents(q, selected));
    } catch {
      setError("콘텐츠를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void searchContents()
      .then(setItems)
      .catch(() => setError("콘텐츠를 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, []);
  function submit(event: FormEvent) {
    event.preventDefault();
    void load();
  }

  return (
    <main className="content-page">
      <SiteHeader compact />
      <section className="content-hero">
        <div className="shell">
          <BookOpen />
          <span className="eyebrow">EXPERT GUIDES</span>
          <h1>건강한 반려 생활 가이드</h1>
          <p>출처와 검토 정보를 확인할 수 있는 운영자 발행 콘텐츠입니다.</p>
        </div>
      </section>
      <section className="content-body shell">
        <form className="content-search" onSubmit={submit}>
          <label>
            <span>검색어</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="건강, 예방접종, 적응…"
            />
          </label>
          <label>
            <span>주제</span>
            <select
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                void load(query, event.target.value);
              }}
            >
              <option value="">전체</option>
              {Object.entries(labels).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button>
            <Search /> 검색
          </button>
        </form>
        <aside className="content-disclaimer">
          의료·행동 관련 글은 일반 정보이며 진단이나 개별 처방을 대신하지
          않습니다. 반려견의 상태는 수의사 또는 자격을 갖춘 전문가와 상담하세요.
        </aside>
        {loading ? (
          <p className="content-empty">콘텐츠를 찾고 있습니다.</p>
        ) : error ? (
          <p className="content-error" role="alert">
            {error}
          </p>
        ) : items.length === 0 ? (
          <p className="content-empty">조건에 맞는 발행 콘텐츠가 없습니다.</p>
        ) : (
          <div className="content-grid">
            {items.map((item) => (
              <article key={item.id}>
                <span>{labels[item.category]}</span>
                <h2>{item.title}</h2>
                <p>{item.summary}</p>
                <small>
                  {item.expertName} · {item.expertCredential}
                  <br />
                  검토 {item.reviewedAt}
                </small>
                <Link href={`/guides/${item.slug}`}>가이드 읽기</Link>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
